import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { classNames } from "../../Utils";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";

import {
  useAuthDispatch,
  useAuthState,
  forgotPassword,
  forgotPasswordSubmit,
} from "../../Context";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";

const RESEND_DELAY = 60;

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { state } = useLocation();
  const { userEmail } = state || {};

  const [email, setemail] = useState(userEmail || "");
  const [password, setpassword] = useState("");
  const [passwordVisible, setpasswordVisible] = useState(false);
  const [code, setcode] = useState("");
  const [timer, setTimer] = useState(0);

  const dispatch = useAuthDispatch();
  const { loading, error, forgotPasswordDetail, passwordSubmitStatus } =
    useAuthState();

  useEffect(() => {
    if (passwordSubmitStatus === "SUCCESS") {
      setTimeout(function () {
        navigate("/login");
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordSubmitStatus]);

  useEffect(() => {
    const storedEndTime = localStorage.getItem("resendCodeEndTime");
    if (storedEndTime) {
      const currentTime = Math.floor(Date.now() / 1000);
      const remainingTime = storedEndTime - currentTime;

      if (remainingTime > 0) {
        setTimer(remainingTime);
      }
    }
  }, []);

  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timer]);

  const handleEmailChange = (event) => {
    setemail(event.target.value);
  };

  const handleCodeChange = (event) => {
    setcode(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setpassword(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    forgotPasswordSubmit(dispatch, { email, code, password });
  };

  const resendCode = () => {
    forgotPassword(dispatch, { email });
    setTimer(RESEND_DELAY);
    const endTime = Math.floor(Date.now() / 1000) + RESEND_DELAY;
    localStorage.setItem("resendCodeEndTime", endTime);
  };

  return (
    <div className="max-w-lg mx-auto">
      {passwordSubmitStatus === "SUCCESS" ? (
        <div className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="mt-3 sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("Password reset successful")}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {t("You can login into your account now.")}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-6">
            <a href="/login">
              <button
                type="button"
                className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
              >
                {t("Go to Login")}
              </button>
            </a>
          </div>
        </div>
      ) : (
        <form
          action="#"
          onSubmit={handleSubmit}
          className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm"
        >
          <p className="text-lg font-medium">{t("Reset your password")}</p>

          {forgotPasswordDetail?.codeDeliveryDetails ? (
            <Alert
              type="success"
              message={`${t("Verification code has been sent using")} ${
                forgotPasswordDetail.codeDeliveryDetails.deliveryMedium
              } ${t("to")} ${
                forgotPasswordDetail.codeDeliveryDetails.destination
              }`}
            />
          ) : null}

          {error ? (
            <Alert
              type="error"
              message={error?.message || "Error reset password."}
              errors={error?.errors}
            />
          ) : null}

          <div>
            <label htmlFor="email" className="text-sm font-medium">
              {t("Email")}
            </label>

            <div className="relative mt-1">
              <input
                type="email"
                id="email"
                name="email"
                className="w-full p-4 pr-12 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500"
                placeholder={t("Enter email")}
                value={email}
                onChange={handleEmailChange}
                required
              />

              <span className="absolute inset-y-0 inline-flex items-center right-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </span>
            </div>
          </div>

          <div>
            <div className="flex justify-between">
              <label htmlFor="one-time-code" className="text-sm font-medium">
                {t("Verification Code")}
              </label>

              <button
                disabled={timer > 0 || !email}
                type="button"
                className={classNames(
                  "text-sm",
                  timer > 0 || !email ? "text-gray-300" : "text-blue-600"
                )}
                onClick={resendCode}
              >
                {t("Send code")}
                {timer > 0 && ` (${timer})`}
              </button>
            </div>

            <div className="relative mt-1">
              <input
                type="number"
                id="one-time-code"
                name="one-time-code"
                className="w-full p-4 pr-12 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500"
                placeholder={t("Enter verification code")}
                value={code}
                onChange={handleCodeChange}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="new-password" className="text-sm font-medium">
              {t("New Password")}
            </label>

            <div className="relative mt-1">
              <input
                type={passwordVisible ? "text" : "password"}
                id="new-password"
                name="new-password"
                className="w-full p-4 pr-12 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500"
                placeholder={t("Enter new password")}
                value={password}
                onChange={handlePasswordChange}
                required
              />

              <button
                type="button"
                onClick={() => setpasswordVisible(!passwordVisible)}
                className="absolute inset-y-0 inline-flex items-center right-4"
              >
                {passwordVisible ? (
                  <EyeIcon className="w-5 h-5 text-gray-400" />
                ) : (
                  <EyeOffIcon className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            loading={loading}
            disabled={loading}
            title={t("Reset")}
          />
        </form>
      )}
    </div>
  );
};

export default ResetPassword;
